// src/components/FloodFrequencyHeatmap.jsx
// 解决方案B：显示洪水频率和强度的热力图（类似Hannah的neighborhood report）

import React from 'react';
import { getThresholdConfig } from '../utils/floodThresholds';

const FloodFrequencyHeatmap = ({ sensorEvents }) => {
  if (!sensorEvents || sensorEvents.length === 0) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 text-center">
        <div className="text-sm text-gray-600 mb-2">
          🗓️ Flood Frequency Pattern
        </div>
        <div className="text-xs text-gray-400">
          No flood events to display
        </div>
      </div>
    );
  }

  // 🔧 生成过去12周的热力图数据
  const generateHeatmapData = (events) => {
    const weeks = 12;
    const daysPerWeek = 7;
    const today = new Date();
    const heatmapData = [];

    // 创建12周x7天的网格
    for (let week = 0; week < weeks; week++) {
      const weekData = [];
      for (let day = 0; day < daysPerWeek; day++) {
        const currentDate = new Date(today);
        currentDate.setDate(today.getDate() - (week * 7 + day));
        
        // 找到这一天的所有洪水事件
        const dayEvents = events.filter(event => {
          const eventDate = new Date(event.timestamp);
          return eventDate.toDateString() === currentDate.toDateString();
        });

        // 计算这一天的最大洪水深度
        const maxDepth = dayEvents.length > 0 
          ? Math.max(...dayEvents.map(e => e.water_depth_inches || 0))
          : 0;

        weekData.push({
          date: currentDate.toISOString().split('T')[0],
          dayOfWeek: day, // 0=周日, 1=周一, ...
          week: week,
          maxDepth: maxDepth,
          eventCount: dayEvents.length,
          severity: maxDepth > 0 ? getSeverityFromDepth(maxDepth) : 'none'
        });
      }
      heatmapData.push(weekData);
    }

    return heatmapData.reverse(); // 最新的周在上面
  };

  // 根据深度获取颜色强度
  const getIntensityColor = (depth, severity) => {
    if (depth === 0) return '#f8f9fa'; // 无洪水：灰白色
    
    const config = getThresholdConfig(severity);
    const baseColor = config.color;
    
    // 根据深度调整透明度
    if (depth <= 3) return baseColor + '40'; // 25% opacity
    if (depth <= 6) return baseColor + '60'; // 38% opacity  
    if (depth <= 12) return baseColor + '80'; // 50% opacity
    return baseColor + 'CC'; // 80% opacity for severe
  };

  const getSeverityFromDepth = (depth) => {
    if (depth < 6) return 'minor';
    if (depth < 12) return 'moderate';
    return 'severe';
  };

  const heatmapData = generateHeatmapData(sensorEvents);
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-3">
      {/* 标题 */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1">
          🗓️ Flood Pattern
          <span className="text-xs font-normal text-gray-500">
            (Last 12 weeks)
          </span>
        </h4>
        <div className="text-xs text-gray-500">
          {sensorEvents.length} total events
        </div>
      </div>

      {/* 热力图网格 */}
      <div className="bg-white border rounded-lg p-3">
        {/* 日期标签 */}
        <div className="grid grid-cols-8 gap-1 mb-2">
          <div></div> {/* 空白角落 */}
          {dayLabels.map(day => (
            <div key={day} className="text-xs text-gray-500 text-center font-medium">
              {day}
            </div>
          ))}
        </div>

        {/* 热力图行 */}
        <div className="space-y-1">
          {heatmapData.map((weekData, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-8 gap-1">
              {/* 周标签 */}
              <div className="text-xs text-gray-400 text-right pr-1 py-1">
                W{weekIndex + 1}
              </div>
              
              {/* 每天的方块 */}
              {weekData.map((dayData) => (
                <div
                  key={dayData.date}
                  className="relative group cursor-pointer"
                  title={`${dayData.date}: ${dayData.maxDepth}" max depth, ${dayData.eventCount} events`}
                >
                  <div
                    className="w-6 h-6 rounded-sm border border-gray-200 transition-all duration-200 hover:scale-110 hover:z-10"
                    style={{
                      backgroundColor: getIntensityColor(dayData.maxDepth, dayData.severity)
                    }}
                  />
                  
                  {/* 悬停提示 */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-20">
                    {new Date(dayData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    <br />
                    {dayData.maxDepth > 0 ? `${dayData.maxDepth}" depth` : 'No flooding'}
                    {dayData.eventCount > 0 && <br />}{dayData.eventCount > 0 && `${dayData.eventCount} events`}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* 图例 */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Less</span>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-gray-100 border border-gray-200 rounded-sm"></div>
              <div className="w-3 h-3 border border-gray-200 rounded-sm" style={{backgroundColor: '#eab30840'}}></div>
              <div className="w-3 h-3 border border-gray-200 rounded-sm" style={{backgroundColor: '#f9731680'}}></div>
              <div className="w-3 h-3 border border-gray-200 rounded-sm" style={{backgroundColor: '#dc2626CC'}}></div>
            </div>
            <span className="text-gray-500">More</span>
          </div>
          <div className="text-center text-xs text-gray-400 mt-2">
            Color intensity shows maximum flood depth per day
          </div>
        </div>
      </div>

      {/* 统计信息 */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center">
          <div className="font-semibold text-blue-900">
            {Math.round(sensorEvents.length / 12 * 10) / 10}
          </div>
          <div className="text-blue-600">Events/week</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded p-2 text-center">
          <div className="font-semibold text-orange-900">
            {Math.max(...sensorEvents.map(e => e.water_depth_inches || 0)).toFixed(1)}"
          </div>
          <div className="text-orange-600">Max depth</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded p-2 text-center">
          <div className="font-semibold text-green-900">
            {Math.round((sensorEvents.filter(e => e.water_depth_inches > 6).length / sensorEvents.length) * 100) || 0}%
          </div>
          <div className="text-green-600">Moderate+</div>
        </div>
      </div>
    </div>
  );
};

export default FloodFrequencyHeatmap;